import { yupResolver } from '@hookform/resolvers/yup'
import { useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { usePhoneValidationSchema } from '@hooks/usePhoneValidationSchema'
import { useHistory } from 'react-router-dom'
import routes from 'src/config/routes'
import { useRequestReturningPatientOtpCode } from '@hooks/useRequestReturningPatientOtpCode'
import {
  canRevalidate,
  initialSmsSent,
  isVerified,
  setInitialSmsSent,
  setPhone,
  useVerifyPhoneContext,
  useVerifyPhoneDispatch,
} from '@context/VerifyPhoneContext'
import { useConfig } from '@context/ConfigurationContext'
import { useLanguage } from '@hooks/useLanguage'

interface FormData {
  phone: string
}

export const usePhoneInput = () => {
  const phoneValidationSchema = usePhoneValidationSchema()
  const history = useHistory()
  const verifyPhoneDispatch = useVerifyPhoneDispatch()
  const { phone, verifyInfo, linkedPatients } = useVerifyPhoneContext()
  const [error, setError] = useState('')
  const { clinicId } = useConfig()
  const lang = useLanguage()

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: { phone },
    resolver: yupResolver(phoneValidationSchema),
  })

  const { mutate: requestOtpCode, isLoading } = useRequestReturningPatientOtpCode({
    onSuccess: () => {
      history.push(routes.oldPatientPhoneVerification)
    },
    onError: (errorTranslationKey: string) => {
      setError(errorTranslationKey)
    },
  })

  const onSubmit: SubmitHandler<FormData> = (data) => {
    const { phone } = data
    verifyPhoneDispatch(setPhone(phone))

    // already verified, no need to check again
    if (isVerified(verifyInfo, phone)) {
      if (linkedPatients.length > 1) {
        history.push(routes.oldPatientSelectAccount)
      } else {
        history.push(routes.appointment)
      }
      // not verified yet because its first attempt or too much time passed after first attempt
    } else if (canRevalidate(verifyInfo, phone)) {
      if (!initialSmsSent(verifyInfo, phone)) {
        verifyPhoneDispatch(setInitialSmsSent(phone))
      }
      requestOtpCode({ phone, clinicId, lang })
    } else {
      // not verified yet but not enough time passed to request second OTP code
      history.push(routes.oldPatientPhoneVerification)
    }
  }

  return {
    error,
    control,
    handleSubmit,
    errors,
    isLoading,
    onSubmit,
  }
}
